import React, { useState, useRef } from 'react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { exportService } from '../../services/exportService';
import { useNotification } from '../../hooks/useNotification';

const BulkImportModal = ({ isOpen, onClose, onSuccess }) => {
  const { showSuccess, showError } = useNotification();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        showError('Please select a CSV file');
        return;
      }
      if (selectedFile.size > 5 * 1024 * 1024) {
        showError('File size must be less than 5MB');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      await exportService.downloadTemplate();
      showSuccess('Template downloaded successfully!');
    } catch (error) {
      showError(error.message || 'Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!file) {
      showError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setResults(null);

    try {
      const response = await exportService.bulkImportDebtors(file);
      setResults(response.data);
      showSuccess(response.message);

      if (response.data.successful > 0) {
        // Delay before calling onSuccess to show results
        setTimeout(() => {
          onSuccess();
        }, 3000);
      }
    } catch (error) {
      showError(error.message || 'Failed to import debtors');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <div className="p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Bulk Import Debtors</h2>

        {!results ? (
          <>
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Instructions:</h3>
              <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                <li>Download the CSV template</li>
                <li>Fill in your debtor data (Name and Mobile are required)</li>
                <li>Upload the completed CSV file</li>
                <li>Review the import results</li>
              </ol>
            </div>

            <div className="mb-6">
              <Button
                onClick={handleDownloadTemplate}
                variant="outline"
                size="sm"
              >
                Download CSV Template
              </Button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select CSV File
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-primary-50 file:text-primary-700
                  hover:file:bg-primary-100"
              />
              {file && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: <span className="font-medium">{file.name}</span> ({(file.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This feature is only available for Basic+ package subscribers.
                The CSV file must contain Name and Mobile columns. Initial debt amounts are optional.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                loading={uploading}
                disabled={!file}
              >
                Import Debtors
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="mb-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-blue-600 font-medium">Total</p>
                  <p className="text-2xl font-bold text-blue-900">{results.total}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-green-600 font-medium">Successful</p>
                  <p className="text-2xl font-bold text-green-900">{results.successful}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-red-600 font-medium">Failed</p>
                  <p className="text-2xl font-bold text-red-900">{results.failed}</p>
                </div>
              </div>

              {results.parseErrors > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-red-900 mb-2">Parse Errors:</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {results.errors.map((error, index) => (
                      <p key={index} className="text-sm text-red-700">
                        Row {error.row}: {error.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {results.details.failed.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-red-900 mb-2">Import Failures:</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                    {results.details.failed.map((failure, index) => (
                      <p key={index} className="text-sm text-red-700">
                        {failure.name} ({failure.mobile}): {failure.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button onClick={handleClose}>
                Close
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default BulkImportModal;
